/* eslint-disable eqeqeq */
import PropTypes from 'prop-types'
import { Box, Button, Card, CardBody, CardHeader, Flex, Heading, Image, Text, VStack, useDisclosure } from '@chakra-ui/react'
import { getProjectExtraDetailByProjectId } from 'api'
import { useEffect, useMemo, useState } from 'react'
import {
  iconEmcModelImpactG3,
  iconEmcModelImpactG3Indoor,
  iconEmcModelCustomNotes,
  iconEmcConfigurationDoubleSided,
  iconEmcConfigurationSingleSided,
  iconEmcPitch4,
  iconEmcPitch6,
  iconEmcPitch9,
  iconEmcPitch10,
  iconEmcPitch15,
  iconEmcPitch20,
  iconEmcCommunicationWireless,
  iconEmcCommunicationEthernet,
  iconEmcCommunicationFiber,
  iconEmcCommunication4g1,
  iconEmcCommunication4g5,
  iconEmcCommunication4gLifetime,
  iconCabinetSeriesXPS,
  iconCabinetSeriesFullDepth,
  iconCabinetConfigurationSingleSided,
  iconCabinetConfigurationDoubleSided,
  iconArtworkConfirmed,
  iconArtworkNotConfirmed,
  iconCphra,
  iconPlpr,
  iconMonumentFoamcraft,
  iconMonumentMasonry,
  iconInstallationPartyClient,
  iconInstallationPartyGrs,
  iconInstallationTypeAnchorBoltRetrofit,
  iconInstallationTypeWallMount,
  iconInstallationTypeNewFooter,
  iconInstallationTypePoleRetrofit,
  iconInstallationTypeMonumentRetrofit,
  iconSiteSurveyClientProvided,
  iconSiteSurveyIncluded,
  iconExistingSignGrs,
  iconExistingSignClient,
  iconEmcModelG4,
  iconEmcModelG5
} from './icons'
import { EMC_FACES, EMC_MODELS, EMC_PITCHES, MONUMENT_STYLES, getAssetUrl, formatFeetInches, formatSquareFootage, getHeightBreakdown, parseStructureComponents, STACK_MODE_LABELS, isTruthyFlag, syncStructureFields } from 'lib'
import { ModalProjectDetailOverview } from './ModalProjectDetailOverview'
import { ModalProjectDetailOverviewHistory } from './ModalProjectDetailOverviewHistory'
import { format as formatDate } from 'date-fns'

const OverviewIcon = ({ label, iconUrl, text = '', iconStyles = null }) => {
  return (
    <VStack gap={2} textAlign="center">
      {iconUrl ? (
        <Image
          src={iconUrl}
          width="100px"
          height="125px"
          {...iconStyles}
        />
      ) : (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="100px"
          height="125px"
          borderWidth={1}
          borderStyle="solid"
          borderColor="gray.300"
        >
          <Text>{text}</Text>
        </Box>
      )}
      <Text fontSize="sm">{label}</Text>
    </VStack>
  )
}

OverviewIcon.propTypes = {
  label: PropTypes.string,
  iconUrl: PropTypes.string,
  text: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]),
  iconStyles: PropTypes.object
}

export const ProjectDetailOverview = ({ projectId, project }) => {
  const [projectExtraDetail, setProjectExtraDetail] = useState(null)
  const disclosure = useDisclosure()
  const disclosureHistory = useDisclosure()

  const load = async () => {
    try {
      const data = await getProjectExtraDetailByProjectId(projectId)
      setProjectExtraDetail(data)
    } catch (err) {
    }
  }

  useEffect(() => {
    load()
  }, [projectId])

  const overviewElements = useMemo(() => {
    const elementsEmc = []
    const elementsCabinet = []
    const elementsPedestalAndStructure = []
    const elementsExtra = []

    if (projectExtraDetail?.noEmc == false) {
      switch (projectExtraDetail?.emcModel) {
        case 'Impact G3':
          elementsEmc.push(<OverviewIcon key="emcModel" label="Model" iconUrl={iconEmcModelImpactG3} />)
          break
        case 'Impact G3 Indoor':
          elementsEmc.push(<OverviewIcon key="emcModel" label="Model" iconUrl={iconEmcModelImpactG3Indoor} />)
          break
        case EMC_MODELS.G4:
          elementsEmc.push(
            <OverviewIcon
              key="emcModel"
              label="Model"
              iconUrl={iconEmcModelG4}
              iconStyles={{
                borderWidth: 1,
                borderStyle: 'solid',
                borderColor: 'gray.300'
              }}
            />)
          break
        case EMC_MODELS.G5:
          elementsEmc.push(
            <OverviewIcon
              key="emcModel"
              label="Model"
              iconUrl={iconEmcModelG5}
              iconStyles={{
                borderWidth: 1,
                borderStyle: 'solid',
                borderColor: 'gray.300'
              }}
            />
          )
          break
        default:
          elementsEmc.push(<OverviewIcon key="emcModel" label="Model" iconUrl={iconEmcModelCustomNotes} />)
          break
      }
      switch (projectExtraDetail?.emcConfiguration) {
        case EMC_FACES.SF:
          elementsEmc.push(<OverviewIcon key="emcConfiguration" label="Configuration" iconUrl={iconEmcConfigurationSingleSided} />)
          break
        case EMC_FACES.DF:
          elementsEmc.push(<OverviewIcon key="emcConfiguration" label="Configuration" iconUrl={iconEmcConfigurationDoubleSided} />)
          break
        default:
          elementsEmc.push(<OverviewIcon key="emcConfiguration" label="Configuration" iconUrl={null} text={projectExtraDetail?.emcConfiguration || 'N/A'} />)
          break
      }
      elementsEmc.push(<OverviewIcon key="emcNominalSize" label="Nominal Size" iconUrl={null} text={projectExtraDetail?.emcNominalSize || 'N/A'} />)
      switch (projectExtraDetail?.emcPitch) {
        case '6.7mm':
          elementsEmc.push(<OverviewIcon key="emcPitch" label="Pitch" iconUrl={iconEmcPitch6} />)
          break
        case '10mm':
          elementsEmc.push(<OverviewIcon key="emcPitch" label="Pitch" iconUrl={iconEmcPitch10} />)
          break
        case '20mm':
          elementsEmc.push(<OverviewIcon key="emcPitch" label="Pitch" iconUrl={iconEmcPitch20} />)
          break
        case EMC_PITCHES.P4:
          elementsEmc.push(<OverviewIcon key="emcPitch" label="Pitch" iconUrl={iconEmcPitch4} />)
          break
        case EMC_PITCHES.P6:
          elementsEmc.push(<OverviewIcon key="emcPitch" label="Pitch" iconUrl={iconEmcPitch6} />)
          break
        case EMC_PITCHES.P9:
          elementsEmc.push(<OverviewIcon key="emcPitch" label="Pitch" iconUrl={iconEmcPitch9} />)
          break
        case EMC_PITCHES.P15:
          elementsEmc.push(<OverviewIcon key="emcPitch" label="Pitch" iconUrl={iconEmcPitch15} />)
          break
        default:
          elementsEmc.push(<OverviewIcon key="emcPitch" label="Pitch" iconUrl={null} text={projectExtraDetail?.emcPitch || 'N/A'} />)
          break
      }
      elementsEmc.push(<OverviewIcon key="emcMatrix" label="Matrix" iconUrl={null} text={projectExtraDetail?.emcMatrix || 'N/A'} />)
      switch (projectExtraDetail?.emcCommunication) {
        case 'Wireless Bridge': case 'Wireless Router':
          elementsEmc.push(<OverviewIcon key="emcCommunication" label="Communication" iconUrl={iconEmcCommunicationWireless} />)
          break
        case 'Cabled Ethernet (20ft)': case 'Ethernet - Client Provided': case 'Ethernet Port':
          elementsEmc.push(<OverviewIcon key="emcCommunication" label="Communication" iconUrl={iconEmcCommunicationEthernet} />)
          break
        case 'Fiber Optic':
          elementsEmc.push(<OverviewIcon key="emcCommunication" label="Communication" iconUrl={iconEmcCommunicationFiber} />)
          break
        case '4G Connection 1Yr & Device':
          elementsEmc.push(<OverviewIcon key="emcCommunication" label="Communication" iconUrl={iconEmcCommunication4g1} />)
          break
        case '4G Connection - 5 Years & Device':
          elementsEmc.push(<OverviewIcon key="emcCommunication" label="Communication" iconUrl={iconEmcCommunication4g5} />)
          break
        case '4G Lifetime Plan & Device':
          elementsEmc.push(<OverviewIcon key="emcCommunication" label="Communication" iconUrl={iconEmcCommunication4gLifetime} />)
          break
        default:
          elementsEmc.push(<OverviewIcon key="emcCommunication" label="Communication" iconUrl={null} text={projectExtraDetail?.emcCommunication || 'N/A'} />)
          break
      }
    }

    if (projectExtraDetail?.noCabinet == false) {
      switch (projectExtraDetail?.cabinetSeries) {
        case 'XPS':
          elementsCabinet.push(<OverviewIcon key="cabinetSeries" label="ID Cabinet" iconUrl={iconCabinetSeriesXPS} />)
          break
        case 'Full':
          elementsCabinet.push(<OverviewIcon key="cabinetSeries" label="ID Cabinet" iconUrl={iconCabinetSeriesFullDepth} />)
          break
        case 'CUSTOM_SHAPE':
          elementsCabinet.push(<OverviewIcon key="cabinetSeries" label="ID Cabinet" iconUrl={null} text="Custom Shape" />)
          break
        default:
          elementsCabinet.push(<OverviewIcon key="cabinetSeries" label="ID Cabinet" iconUrl={null} text={projectExtraDetail?.cabinetSeries || 'N/A'} />)
          break
      }
      switch (projectExtraDetail?.cabinetConfiguration) {
        case 'Single Face':
          elementsCabinet.push(<OverviewIcon key="cabinetConfiguration" label="Configuration" iconUrl={iconCabinetConfigurationSingleSided} />)
          break
        case 'Double Face':
          elementsCabinet.push(<OverviewIcon key="cabinetConfiguration" label="Configuration" iconUrl={iconCabinetConfigurationDoubleSided} />)
          break
        default:
          elementsCabinet.push(<OverviewIcon key="cabinetConfiguration" label="Configuration" iconUrl={null} text={projectExtraDetail?.cabinetConfiguration || 'N/A'} />)
          break
      }
      elementsCabinet.push(<OverviewIcon key="cabinetNominalSize" label="Nominal Size" iconUrl={null} text={projectExtraDetail?.cabinetNominalSize || 'N/A'} />)
      switch (projectExtraDetail?.signedArtwork) {
        case 'Yes':
          elementsCabinet.push(<OverviewIcon key="signedArtwork" label="Artwork" iconUrl={iconArtworkConfirmed} />)
          break
        case 'No':
          elementsCabinet.push(<OverviewIcon key="signedArtwork" label="Artwork" iconUrl={iconArtworkNotConfirmed} />)
          break
        default:
          elementsCabinet.push(<OverviewIcon key="signedArtwork" label="Artwork" iconUrl={null} text={projectExtraDetail?.signedArtwork || 'N/A'} />)
          break
      }
      if (projectExtraDetail?.cphra) {
        elementsCabinet.push(<OverviewIcon key="cphra" label="Vector Art" iconUrl={iconCphra} />)
      }
      if (projectExtraDetail?.plpr) {
        elementsCabinet.push(<OverviewIcon key="plpr" label="Vector Art" iconUrl={iconPlpr} />)
      }
    }

    if (projectExtraDetail?.noPedestal == false) {
      elementsPedestalAndStructure.push(<OverviewIcon key="pedestalStyle" label="Pedestal Style" iconUrl={null} text={projectExtraDetail?.pedestalStyle || 'N/A'} />)
      elementsPedestalAndStructure.push(<OverviewIcon key="pedestalNominalSize" label="Pedestal Size" iconUrl={null} text={projectExtraDetail?.pedestalNominalSize || 'N/A'} />)
    } else {
      elementsPedestalAndStructure.push(<OverviewIcon key="pedestalAdditionalHeight" label="Additional Pole Height" iconUrl={null} text={`${projectExtraDetail?.pedestalAdditionalHeight}` || 'N/A'} />)
    }
    const isWallMountSupport = /wall mount/i.test(projectExtraDetail?.installationOption || '') ||
      /wall mount brackets/i.test(projectExtraDetail?.verticalSupportOption || '')
    elementsPedestalAndStructure.push(<OverviewIcon key="numberOfSupports" label={isWallMountSupport ? 'Bracket Pairs' : '# of Supports'} iconUrl={null} text={projectExtraDetail?.numberOfSupports || 'N/A'} />)
    elementsPedestalAndStructure.push(<OverviewIcon key="verticalSupportOption" label="Support Size" iconUrl={null} text={projectExtraDetail?.verticalSupportOption || 'N/A'} />)
    elementsPedestalAndStructure.push(<OverviewIcon key="footerStyleOption" label="Footer Style" iconUrl={null} text={projectExtraDetail?.footerStyleOption || 'N/A'} />)

    if (projectExtraDetail?.noMonument == false) {
      switch (projectExtraDetail?.monumentStyle) {
        case MONUMENT_STYLES.DuraCore:
          elementsExtra.push(<OverviewIcon key="monumentStyle" label="Monument" iconUrl={iconMonumentFoamcraft} />)
          break
        case MONUMENT_STYLES.Masonry:
          elementsExtra.push(<OverviewIcon key="monumentStyle" label="Monument" iconUrl={iconMonumentMasonry} />)
          break
        default:
          elementsExtra.push(<OverviewIcon key="monumentStyle" label="Monument" iconUrl={null} text={projectExtraDetail?.monumentStyle || 'N/A'} />)
          break
      }
    }
    switch (projectExtraDetail?.installationOption) {
      case 'Client Installation Anchor Bolt Kit / Guide':
      case 'Client Installation':
      case 'Self Install/Client Install':
        elementsExtra.push(<OverviewIcon key="installationParty" label="Installation Party" iconUrl={iconInstallationPartyClient} />)
        break
      case 'Professional Installation Included - Wall Mount':
      case 'Professional Installation - New Footer':
      case 'Professional Installation - Retro Fit':
      case 'Professional Installation New Footer - V2':
        elementsExtra.push(<OverviewIcon key="installationParty" label="Installation Party" iconUrl={iconInstallationPartyGrs} />)
        break
      default:
        elementsExtra.push(<OverviewIcon key="installationParty" label="Installation Party" iconUrl={null} text={projectExtraDetail?.installationOption || 'N/A'} />)
        break
    }
    switch (projectExtraDetail?.installationOption) {
      case 'Client Installation Anchor Bolt Kit / Guide':
        elementsExtra.push(<OverviewIcon key="installationType" label="Installation Type" iconUrl={iconInstallationTypeAnchorBoltRetrofit} />)
        break
      case 'Professional Installation Included - Wall Mount':
        elementsExtra.push(<OverviewIcon key="installationType" label="Installation Type" iconUrl={iconInstallationTypeWallMount} />)
        break
      case 'Professional Installation - New Footer':
      case 'Professional Installation New Footer - V2':
        elementsExtra.push(<OverviewIcon key="installationType" label="Installation Type" iconUrl={iconInstallationTypeNewFooter} />)
        break
      case 'Professional Installation - Retro Fit':
        if (projectExtraDetail?.noMonument == true) {
          elementsExtra.push(<OverviewIcon key="installationType" label="Installation Type" iconUrl={iconInstallationTypePoleRetrofit} />)
        } else {
          elementsExtra.push(<OverviewIcon key="installationType" label="Installation Type" iconUrl={iconInstallationTypeMonumentRetrofit} />)
        }
        break
      default:
        elementsExtra.push(<OverviewIcon key="installationType" label="Installation Type" iconUrl={null} text={projectExtraDetail?.installationOption || 'N/A'} />)
        break
    }
    switch (projectExtraDetail?.siteSurveyOption) {
      case 'Client Providing Measurements':
        elementsExtra.push(<OverviewIcon key="siteSurveyOption" label="Site Survey" iconUrl={iconSiteSurveyClientProvided} />)
        break
      case 'Site Survey L1':
      case 'Site Survey L2':
      case 'Site Survey L3':
        elementsExtra.push(<OverviewIcon key="siteSurveyOption" label="Site Survey" iconUrl={iconSiteSurveyIncluded} />)
        break
    }
    switch (projectExtraDetail?.existingSignOption) {
      case 'Removal  included  V1':
      case 'Removal  included - V2':
      case 'Removal  included - V3':
      case 'Removal  included - see line item':
        elementsExtra.push(<OverviewIcon key="existingSignOption" label="Existing Sign" iconUrl={iconExistingSignGrs} />)
        break
      case 'Not Applicable':
        elementsExtra.push(<OverviewIcon key="existingSignOption" label="Existing Sign" iconUrl={iconExistingSignClient} />)
        break
      default:
        elementsExtra.push(<OverviewIcon key="existingSignOption" label="Existing Sign" iconUrl={null} text={projectExtraDetail?.existingSignOption || 'N/A'} />)
        break
    }
    if (projectExtraDetail?.mowCurbEnabled) {
      const calculationType = projectExtraDetail?.mowCurbCalculationType === 'pedestal' ? 'Based on Pedestal' : 'Manual Entry'
      elementsExtra.push(<OverviewIcon key="mowCurb" label="Mow Curb" iconUrl={null} text={`${calculationType} - ${projectExtraDetail?.mowCurbWidthFt || 0} ft`} />)
    }

    const extraComponents = parseStructureComponents(projectExtraDetail?.structureComponents)
    const structureInput = {
      ...projectExtraDetail,
      structureComponents: extraComponents
    }
    const synced = syncStructureFields(structureInput)
    const breakdown = getHeightBreakdown({ ...structureInput, groundClearanceFt: synced.groundClearanceFt })
    const elementsStructureHeight = [
      <OverviewIcon key="groundClearance" label="Ground Clearance" iconUrl={null} text={formatFeetInches(synced.groundClearanceFt)} />,
      <OverviewIcon key="overallHeight" label="Overall Height (OAH)" iconUrl={null} text={formatFeetInches(synced.overallHeightFt)} />,
      <OverviewIcon key="squareFootage" label="Square Footage" iconUrl={null} text={formatSquareFootage(synced.squareFootage)} />
    ]
    breakdown.filter(row => row.key !== 'groundClearance').forEach(row => {
      elementsStructureHeight.push(
        <OverviewIcon
          key={row.key}
          label={row.label}
          iconUrl={null}
          text={`${formatFeetInches(row.heightFt)}${row.stackMode ? ` · ${STACK_MODE_LABELS[row.stackMode]}` : ''}${row.included ? '' : ' (excluded)'}`}
        />
      )
    })
    if (isTruthyFlag(projectExtraDetail?.groundClearanceManual) || isTruthyFlag(projectExtraDetail?.overallHeightManual) || isTruthyFlag(projectExtraDetail?.squareFootageManual)) {
      elementsStructureHeight.push(<OverviewIcon key="oahOverride" label="Manual Override" iconUrl={null} text="Yes" />)
    }
    if (projectExtraDetail?.fileSignedQuote) {
      elementsExtra.push(<OverviewIcon key="fileSignedQuote" label="Signed Quote" iconUrl={null} text={<Button size="sm" variant="link" color="blue" onClick={() => window.open(getAssetUrl(projectExtraDetail.fileSignedQuote))}>View</Button>} />)
    } else {
      elementsExtra.push(<OverviewIcon key="fileSignedQuote" label="Signed Quote" iconUrl={null} text="N/A" />)
    }
    if (projectExtraDetail?.fileSignedArt) {
      elementsExtra.push(<OverviewIcon key="fileSignedArt" label="Signed Art" iconUrl={null} text={<Button size="sm" variant="link" color="blue" onClick={() => window.open(getAssetUrl(projectExtraDetail.fileSignedArt))}>View</Button>} />)
    } else {
      elementsExtra.push(<OverviewIcon key="fileSignedArt" label="Signed Art" iconUrl={null} text="N/A" />)
    }

    const elementsAccounting = []
    elementsAccounting.push(<OverviewIcon key="customerPo" label="PO" iconUrl={null} text={projectExtraDetail?.customerPo || 'N/A'} />)
    if (projectExtraDetail?.fileTaxExemption) {
      elementsAccounting.push(<OverviewIcon key="fileTaxExemption" label="Tax Exemption" iconUrl={null} text={<Button size="sm" variant="link" color="blue" onClick={() => window.open(getAssetUrl(projectExtraDetail.fileTaxExemption))}>View</Button>} />)
    } else {
      elementsAccounting.push(<OverviewIcon key="fileTaxExemption" label="Tax Exemption" iconUrl={null} text="N/A" />)
    }
    if (projectExtraDetail?.isIndividualSchool) {
      elementsAccounting.push(<OverviewIcon key="schoolDistrictName" label="School District" iconUrl={null} text={projectExtraDetail?.schoolDistrictName || 'N/A'} />)
    }
    elementsAccounting.push(<OverviewIcon key="expectedPaymentTerms" label="Payment Terms" iconUrl={null} text={projectExtraDetail?.expectedPaymentTerms || 'N/A'} />)
    elementsAccounting.push(<OverviewIcon key="customerProvidingDeposit" label="Customer Deposit" iconUrl={null} text={projectExtraDetail?.customerProvidingDeposit || 'N/A'} />)

    return {
      elementsEmc,
      elementsCabinet,
      elementsPedestalAndStructure,
      elementsExtra,
      elementsAccounting,
      elementsStructureHeight
    }
  }, [projectExtraDetail])

  const onEditOverview = () => {
    disclosure.onOpen()
  }

  const scopeUpdatedAt = useMemo(() => {
    if (projectExtraDetail?.scopeUpdatedAt) {
      return projectExtraDetail.scopeUpdatedAt
    } else if (projectExtraDetail?.createdAt) {
      return projectExtraDetail.createdAt
    } else {
      return null
    }
  }, [projectExtraDetail])

  return (
    <>
      <VStack alignItems="none" gap={4}>
        <Heading size="md" mb={4}>{project?.customerCompany}</Heading>
        <Card>
          <CardHeader bg="gray.200" display="flex" justifyContent="space-between">
            <Heading size="sm">Scope of Work</Heading>
            <Text fontSize="xs">Scope updated at {scopeUpdatedAt ? formatDate(new Date(scopeUpdatedAt), 'MM/dd/yyyy hh:mm:ss') : 'N/A'}</Text>
          </CardHeader>
          <CardBody>
            <Text fontSize="sm">{projectExtraDetail?.scope}</Text>
          </CardBody>
        </Card>
        <Card>
          <CardHeader bg="gray.200"><Heading size="sm">Overall Structure Height</Heading></CardHeader>
          <CardBody>
            <Flex flexWrap="wrap" gap={4}>
              {overviewElements.elementsStructureHeight}
            </Flex>
          </CardBody>
        </Card>
        {projectExtraDetail?.noEmc == false && (
          <Card>
            <CardHeader bg="gray.200"><Heading size="sm">EMC Details</Heading></CardHeader>
            <CardBody>
              <Flex flexWrap="wrap" gap={4}>
                {overviewElements.elementsEmc}
              </Flex>
            </CardBody>
          </Card>
        )}
        {projectExtraDetail?.noCabinet == false && (
        <Card>
          <CardHeader bg="gray.200"><Heading size="sm">Cabinet Details</Heading></CardHeader>
          <CardBody>
            <Flex flexWrap="wrap" gap={4}>
              {overviewElements.elementsCabinet}
            </Flex>
          </CardBody>
        </Card>
        )}
        <Card>
          <CardHeader bg="gray.200"><Heading size="sm">Pedestal & Structure Details</Heading></CardHeader>
          <CardBody>
            <Flex flexWrap="wrap" gap={4}>
              {overviewElements.elementsPedestalAndStructure}
            </Flex>
          </CardBody>
        </Card>
        <Card>
          <CardHeader bg="gray.200"><Heading size="sm">Extra Details</Heading></CardHeader>
          <CardBody>
            <Flex flexWrap="wrap" gap={4}>
              {overviewElements.elementsExtra}
            </Flex>
          </CardBody>
        </Card>
        <Card>
          <CardHeader bg="gray.200"><Heading size="sm">Accounting Information</Heading></CardHeader>
          <CardBody>
            <Flex flexWrap="wrap" gap={4}>
              {overviewElements.elementsAccounting}
            </Flex>
            <Flex flexWrap="wrap" gap={6} mt={4} width="100%">
              <Box flex="1 1 280px" minW={0}>
                <Text fontSize="sm" fontWeight="semibold">Alt. Accounting Contact</Text>
                <Text fontSize="sm" whiteSpace="pre-wrap" overflowWrap="anywhere">{projectExtraDetail?.alternateAccountingContact || 'N/A'}</Text>
              </Box>
              <Box flex="1 1 280px" minW={0}>
                <Text fontSize="sm" fontWeight="semibold">Notes</Text>
                <Text fontSize="sm" whiteSpace="pre-wrap" overflowWrap="anywhere">{projectExtraDetail?.accountingNotes || 'N/A'}</Text>
              </Box>
            </Flex>
          </CardBody>
        </Card>
        <Flex gap={2}>
          <Button colorScheme="blue" onClick={onEditOverview}>Edit Overview</Button>
          {projectExtraDetail?.id && (
            <Button variant="outline" onClick={disclosureHistory.onOpen}>View History</Button>
          )}
        </Flex>
      </VStack>
      <ModalProjectDetailOverview
        projectId={projectId}
        projectExtraDetail={projectExtraDetail}
        reload={load}
        {...disclosure}
      />
      <ModalProjectDetailOverviewHistory
        projectExtraDetailId={projectExtraDetail?.id}
        {...disclosureHistory}
      />
    </>
  )
}

ProjectDetailOverview.propTypes = {
  projectId: PropTypes.number,
  project: PropTypes.object
}
